import { useState } from 'react';
import {
  ADDED_BEYOND_BRIEF,
  MAPPING_TO_CONFIRM,
  contrastRatio,
  cssVar,
  elevation,
  meetsAA,
  motion,
  radius,
  size,
  space,
  themes,
  type,
  type Palette,
} from '@going-once/tokens';
import { BidState, Box, Card, Field, Pressable, Select, Sheet, Stack, Text } from '../ui/primitives';
import { AdminPrimitives } from './GalleryAdmin';

/**
 * Every token and every primitive, on one page.
 *
 * **This is the handoff artifact.** The brief says "hand back a partial token set early
 * rather than a complete one late… we can show you the whole app in your colors the same
 * day" — this is what makes that true. It is also the contrast audit the brief asks for,
 * computed live rather than eyeballed, so replacing a value shows immediately whether it
 * still clears AA.
 *
 * It is a developer surface, not a product screen: it is allowed to be dense.
 */
export function Gallery() {
  const [theme, setTheme] = useState<'dark' | 'light'>(
    (document.documentElement.dataset.theme as 'light' | undefined) ?? 'dark',
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const [fieldValue, setFieldValue] = useState('Levi’s 501');

  function applyTheme(next: 'dark' | 'light'): void {
    setTheme(next);
    if (next === 'light') document.documentElement.dataset.theme = 'light';
    else delete document.documentElement.dataset.theme;
  }

  return (
    <Stack gap="2xl">
      <Stack direction="row" justify="between" align="center" wrap>
        <Stack gap="xs">
          <Text as="h1" step="display">
            Tokens &amp; primitives
          </Text>
          <Text step="label" tone="fg.muted">
            Slot names come from the Design System Brief. The values are placeholders — replace them, keep the names.
          </Text>
        </Stack>
        <Stack direction="row" gap="sm">
          <Pressable variant={theme === 'dark' ? 'primary' : 'secondary'} onPress={() => applyTheme('dark')}>
            Dark
          </Pressable>
          <Pressable variant={theme === 'light' ? 'primary' : 'secondary'} onPress={() => applyTheme('light')}>
            Light
          </Pressable>
        </Stack>
      </Stack>

      <Section title="Color" note="Semantic slots. ⚡ marks the ones the live show depends on.">
        <Stack gap="sm">
          {(Object.keys(themes[theme]) as (keyof Palette)[]).map((slot) => (
            <Swatch key={slot} slot={slot} palette={themes[theme]} />
          ))}
        </Stack>
      </Section>

      <Section
        title="Contrast"
        note="Computed, not eyeballed. AA is 4.5:1 for body text. The scrim is measured over a white video frame — the worst case."
      >
        <Stack gap="xs">
          {CONTRAST_PAIRS.map(({ fg, bg, backdrop }) => {
            const ratio = contrastRatio(themes[theme][fg], themes[theme][bg], backdrop);
            return (
              <Stack key={`${fg}-${bg}`} direction="row" justify="between" gap="md">
                <Text step="mono">
                  {fg} on {bg}
                </Text>
                <Text step="mono" tone={meetsAA(ratio) ? 'success' : 'danger'}>
                  {ratio.toFixed(2)}:1 {meetsAA(ratio) ? 'AA' : 'FAILS AA'}
                </Text>
              </Stack>
            );
          })}
        </Stack>
      </Section>

      <Section title="Type" note="Whole steps: size, line height and weight travel together.">
        <Stack gap="md">
          {(Object.keys(type) as (keyof typeof type)[]).map((step) => (
            <Stack key={step} gap="xs">
              <Text step="mono" tone="fg.muted">
                type.{step} — {type[step].size}/{type[step].line}/{type[step].weight}
              </Text>
              <Text step={step}>{step === 'countdown' ? '00:09' : 'Going once, going twice'}</Text>
            </Stack>
          ))}
        </Stack>
      </Section>

      <Section title="Space, radius, size" note="Unitless numbers in the contract; the renderer adds the unit.">
        <Stack direction="row" gap="lg" wrap align="end">
          {(Object.keys(space) as (keyof typeof space)[]).map((step) => (
            <Stack key={step} gap="xs" align="center">
              <Box
                background="accent.subtle"
                style={{ width: cssVar(`space.${step}`), height: cssVar('size.thumbnail') }}
              />
              <Text step="mono" tone="fg.muted">
                {step} · {space[step]}
              </Text>
            </Stack>
          ))}
        </Stack>
        <Stack direction="row" gap="lg" wrap>
          {(Object.keys(radius) as (keyof typeof radius)[]).map((step) => (
            <Stack key={step} gap="xs" align="center">
              <Box
                background="accent.subtle"
                radius={step}
                style={{ width: cssVar('size.target.min'), height: cssVar('size.target.min') }}
              />
              <Text step="mono" tone="fg.muted">
                {step} · {radius[step]}
              </Text>
            </Stack>
          ))}
        </Stack>
        <Text step="mono" tone="fg.muted">
          {Object.entries(size)
            .map(([slot, value]) => `size.${slot} ${value}`)
            .join('   ')}
        </Text>
      </Section>

      <Section title="Elevation & motion" note="Structured values, so React Native can build them too.">
        <Stack direction="row" gap="lg" wrap>
          {(Object.keys(elevation) as (keyof typeof elevation)[]).map((step) => (
            <Box key={step} background="bg.raised" radius="lg" padding="md" shadow={step}>
              <Text step="mono">elevation.{step}</Text>
            </Box>
          ))}
        </Stack>
        <Text step="mono" tone="fg.muted">
          {Object.entries(motion.duration)
            .map(([slot, value]) => `duration.${slot} ${value}ms`)
            .join('   ')}
          {'   '}
          {Object.keys(motion.easing)
            .map((slot) => `easing.${slot}`)
            .join('   ')}
        </Text>
      </Section>

      <Section
        title="Primitives, in every state"
        note="Hover and press are live — try them. Tab to see the focus ring."
      >
        <Stack gap="md">
          <Stack direction="row" gap="sm" wrap>
            {(['primary', 'secondary', 'quiet', 'danger'] as const).map((variant) => (
              <Pressable key={variant} variant={variant} onPress={() => undefined}>
                {variant}
              </Pressable>
            ))}
            <Pressable variant="primary" disabled onPress={() => undefined}>
              disabled
            </Pressable>
            <Pressable variant="primary" busy onPress={() => undefined}>
              loading
            </Pressable>
          </Stack>

          <Stack direction="row" gap="md" wrap align="start">
            <Box grow>
              <Field id="g-field" label="Field" value={fieldValue} onChange={setFieldValue} />
            </Box>
            <Box grow>
              <Field
                id="g-field-error"
                label="Field, in error"
                value=""
                onChange={() => undefined}
                problem="Brand is required before this listing can be published."
              />
            </Box>
            <Box grow>
              <Select
                id="g-select"
                label="Select"
                value="a"
                onChange={() => undefined}
                options={[{ value: 'a', label: 'fashion/outerwear' }]}
                note="Changing this changes which details are asked for."
              />
            </Box>
          </Stack>

          <Stack direction="row" gap="lg" align="center" wrap>
            <Card>
              <Text>Card</Text>
            </Card>
            {/* ⚡ Two channels, not one: the brief requires this pair to be readable
                without color, because it says whether money is about to move. */}
            <BidState state="leading" />
            <BidState state="outbid" />
            <Pressable variant="secondary" onPress={() => setSheetOpen(true)}>
              Open sheet
            </Pressable>
          </Stack>
        </Stack>
      </Section>

      <Section
        title="Admin console primitives"
        note="The admin Sellers page's pieces (GO-613), in their states. Made-up rows; nothing here calls the API. The consoles are pinned dark — use Light above to check the other palette anyway."
      >
        <AdminPrimitives />
      </Section>

      <Section
        title="Provisional — inferences to confirm"
        note="The dark palette is extracted from Ciera's mockup, which carries no token names. Every line here is engineering reading intent out of a hex code."
      >
        <Stack gap="sm">
          {MAPPING_TO_CONFIRM.map((question) => (
            <Text key={question} step="label" tone="fg.muted">
              • {question}
            </Text>
          ))}
        </Stack>
      </Section>

      <Section
        title="Added beyond the brief"
        note="Slots we needed that the brief does not name. These go back to design."
      >
        <Text step="mono" tone="fg.muted">
          {ADDED_BEYOND_BRIEF.join('   ')}
        </Text>
      </Section>

      {sheetOpen ? (
        <Sheet onDismiss={() => setSheetOpen(false)}>
          <Stack gap="md" align="start">
            <Text step="heading">Sheet</Text>
            <Text tone="fg.muted">Sits on `elevation.overlay` over `color.overlay.scrim`.</Text>
            <Pressable variant="primary" onPress={() => setSheetOpen(false)}>
              Close
            </Pressable>
          </Stack>
        </Sheet>
      ) : null}
    </Stack>
  );
}

const CONTRAST_PAIRS: { fg: keyof Palette; bg: keyof Palette; backdrop?: string }[] = [
  { fg: 'fg.default', bg: 'bg.base' },
  { fg: 'fg.default', bg: 'bg.raised' },
  { fg: 'fg.muted', bg: 'bg.base' },
  { fg: 'fg.inverse', bg: 'accent.default' },
  { fg: 'bid.leading', bg: 'bg.base' },
  { fg: 'bid.outbid', bg: 'bg.base' },
  { fg: 'live', bg: 'bg.base' },
  { fg: 'danger', bg: 'bg.base' },
  // token-exempt: not a design value — the brightest possible video frame, as an audit input
  { fg: 'overlay.fg', bg: 'overlay.scrim', backdrop: '#ffffff' },
];

function Swatch({ slot, palette }: { slot: keyof Palette; palette: Palette }) {
  const critical = ['live', 'bid.leading', 'bid.outbid', 'overlay.scrim', 'overlay.fg'].includes(slot);
  return (
    <Stack direction="row" gap="md" align="center">
      <Box
        border
        radius="sm"
        style={{ width: cssVar('size.target.min'), height: cssVar('size.target.min'), background: palette[slot] }}
      />
      <Text step="mono">
        {critical ? '⚡ ' : ''}
        color.{slot}
      </Text>
      <Text step="mono" tone="fg.muted">
        {palette[slot]}
      </Text>
    </Stack>
  );
}

function Section({ title, note, children }: { title: string; note?: string; children?: React.ReactNode }) {
  return (
    <Stack gap="md">
      <Stack gap="xs">
        <Text as="h2" step="heading">
          {title}
        </Text>
        {note ? (
          <Text step="label" tone="fg.muted">
            {note}
          </Text>
        ) : null}
      </Stack>
      {children}
    </Stack>
  );
}
